import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  breathPatternLabel,
  BREATH_PHASES,
  BREATH_PHASE_CUES,
  COACHING_BREATHWORK_OUTCOME_LABELS,
  type BreathPhase,
  type CoachingBreathworkOutcome,
} from '@mlm/shared/coaching';
import { cn } from '@mlm/ui/lib/cn';
import { BreathPacer } from '../components/breath-pacer';
import { linkButton } from '../components/panels/today';
import { Block, Chip, ErrorNote, GoldSeal, Loading, Row, Screen } from '../components/primitives';
import { MoreWithPlan, ProLock } from '../components/pro-lock';
import { coaching, type BreathworkPattern } from '../lib/api';
import { clockLabel, durationLabel } from '../lib/format';
import { useBreathwork, useRefreshCoaching, useFeatureLocked } from '../lib/queries';
import { useBreathSession } from '../lib/use-breath-session';
import { darkPillGlass, darkPillGold, ImmersiveFrame, ToolFrame } from './tool-frame';

const outcomeLabel = (o: string) =>
  COACHING_BREATHWORK_OUTCOME_LABELS[o as CoachingBreathworkOutcome] ?? o;

const patternMeta = (p: BreathworkPattern) =>
  [
    breathPatternLabel(p.pattern),
    `${p.cycles} ${p.cycles === 1 ? 'round' : 'rounds'}`,
    durationLabel(p.totalSeconds),
  ]
    .filter(Boolean)
    .join(' · ');

/** SC-30: outcome first. The member picks how they want to feel, then a pattern. */
export function BreathworkPage() {
  const locked = useFeatureLocked('breathwork');
  const library = useBreathwork();
  const [outcome, setOutcome] = useState<string | null>(null);

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Breathwork" title="How do you want to feel?">
        <ProLock feature="breathwork" />
      </Screen>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;

  const { patterns, outcomes, proOnlyCount } = library.data;
  const offered = outcomes.filter((o) => patterns.some((p) => p.outcome === o));
  const shown = outcome ? patterns.filter((p) => p.outcome === outcome) : patterns;

  return (
    <Screen
      back="/align"
      eyebrow="Breathwork"
      title="How do you want to feel?"
      subtitle="Choose an outcome, then follow the ring at your own pace."
    >
      <div className="flex flex-wrap gap-2 border-b border-border px-5 py-5">
        <Chip selected={outcome === null} onClick={() => setOutcome(null)}>
          All
        </Chip>
        {offered.map((o) => (
          <Chip key={o} selected={outcome === o} onClick={() => setOutcome(o)}>
            {outcomeLabel(o)}
          </Chip>
        ))}
      </div>
      {shown.map((p) => (
        <Row
          key={p.id}
          title={p.title}
          meta={`${outcomeLabel(p.outcome)} · ${patternMeta(p)}`}
          to={`/align/breathwork/${encodeURIComponent(p.id)}`}
        />
      ))}
      <MoreWithPlan count={proOnlyCount} noun="pattern" />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        Breathe gently and never force a hold. Stop and breathe normally if you feel dizzy or
        uncomfortable.
      </p>
    </Screen>
  );
}

/** One guided session: the ring, the cue, the round, and the practice recorded at the end. */
export function BreathworkSessionPage() {
  const { id = '' } = useParams();
  const locked = useFeatureLocked('breathwork');
  const library = useBreathwork();

  if (locked) {
    return (
      <ToolFrame close="/align/breathwork">
        <ProLock feature="breathwork" />
      </ToolFrame>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;
  const pattern = library.data.patterns.find((p) => p.id === id);
  if (!pattern) {
    return (
      <ToolFrame close="/align/breathwork">
        <Block
          title="This practice is not available"
          action={
            <Link to="/align/breathwork" className={linkButton}>
              Choose another
            </Link>
          }
        >
          It may have been changed or removed. Choose another breathing practice.
        </Block>
      </ToolFrame>
    );
  }
  return <Session pattern={pattern} />;
}

const PHASE_SHORT: Record<BreathPhase, string> = {
  inhale: 'In',
  holdIn: 'Hold',
  exhale: 'Out',
  holdOut: 'Hold',
};

/** The redesign's immersive session: the pacer, the round, the phases, and the practice recorded at the end. */
function Session({ pattern }: { pattern: BreathworkPattern }) {
  const refresh = useRefreshCoaching();
  const { status, position, ring, start, pause, resume, reset } = useBreathSession(
    pattern.pattern,
    pattern.cycles,
  );
  const recorded = useRef(false);
  const complete = useMutation({
    mutationFn: () => coaching.practice('breathwork'),
    meta: { silentErrorCodes: ['coaching_pro_required'] },
    onSuccess: () => void refresh(),
  });

  // The practice counts when the session reaches its end, once.
  useEffect(() => {
    if (status !== 'done' || recorded.current) return;
    recorded.current = true;
    coaching.event('breathwork_completed', {
      pattern: pattern.id,
      cycles: pattern.cycles,
      seconds: pattern.totalSeconds,
    });
    complete.mutate();
  }, [status, pattern, complete]);

  // Keep the screen on while breathing; a phone that dims mid-session loses the member.
  const running = status === 'running';
  useEffect(() => {
    if (!running || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let released = false;
    navigator.wakeLock
      .request('screen')
      .then((l) => {
        if (released) void l.release();
        else lock = l;
      })
      .catch(() => undefined);
    return () => {
      released = true;
      void lock?.release();
    };
  }, [running]);

  const remaining = Math.max(0, (position.totalMs - position.elapsedMs) / 1000);
  const total = clockLabel(pattern.totalSeconds);
  const rounds = `${pattern.cycles} ${pattern.cycles === 1 ? 'round' : 'rounds'}`;
  const cue =
    status === 'idle'
      ? 'Ready when you are'
      : status === 'done'
        ? 'Breathe normally'
        : status === 'paused'
          ? 'Paused'
          : BREATH_PHASE_CUES[position.phase];
  const roundLine =
    status === 'idle'
      ? `${rounds} · ${total}`
      : status === 'done'
        ? `${rounds} done · ${total}`
        : `Round ${position.cycle} of ${pattern.cycles} · ${clockLabel(Math.ceil(remaining))} left`;
  const active = status === 'running' || status === 'paused';
  const progress =
    status === 'idle'
      ? 0
      : Math.min(100, (position.elapsedMs / Math.max(1, position.totalMs)) * 100);

  return (
    <ImmersiveFrame
      close="/align/breathwork"
      ground="session"
      eyebrow={`Breathwork · ${outcomeLabel(pattern.outcome)}`}
    >
      <div className="mt-[26px] text-center">
        <h1 className="font-display text-[30px] font-medium leading-[1.1] tracking-[-0.01em]">
          {pattern.title}
        </h1>
        <p className="mt-2.5 font-mono text-[0.68rem] font-medium uppercase tracking-[0.14em] text-paper/55">
          {patternMeta(pattern)}
        </p>
      </div>

      <BreathPacer
        pattern={pattern.pattern}
        status={status}
        position={position}
        ring={ring}
        cue={cue}
      />

      <p className="mt-[18px] min-h-4 text-center font-mono text-xs tabular-nums tracking-[0.06em] text-paper/60">
        {roundLine}
      </p>

      <ul className="mx-auto mt-[18px] flex flex-wrap justify-center gap-2" aria-label="The breath">
        {BREATH_PHASES.filter((phase) => pattern.pattern[phase] > 0).map((phase) => {
          const current = active && position.phase === phase;
          return (
            <li
              key={phase}
              className={cn(
                'inline-flex items-baseline gap-1.5 rounded-full border px-3 py-[7px] font-mono text-[0.64rem] uppercase tracking-[0.12em]',
                current
                  ? 'border-[rgba(230,200,122,0.7)] bg-[rgba(230,200,122,0.14)] text-[#E6C87A]'
                  : 'border-paper/[0.14] text-paper/60',
              )}
            >
              <span>{PHASE_SHORT[phase]}</span>
              <span className="text-[0.8rem] tracking-normal">{pattern.pattern[phase]}s</span>
            </li>
          );
        })}
      </ul>

      <div className="mt-[22px] h-0.5 overflow-hidden rounded-sm bg-paper/10" aria-hidden>
        <div
          className="h-0.5 rounded-sm bg-gradient-to-r from-verde to-[#E6C87A] transition-[width] duration-300 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {status === 'idle' && pattern.description && (
        <p className="mx-auto mt-5 max-w-[36ch] text-center text-[13px] leading-relaxed text-paper/60">
          {pattern.description}
        </p>
      )}

      {status === 'done' && (
        <div className="mt-[22px] flex items-center gap-3.5 rounded-[18px] border border-paper/10 bg-paper/[0.06] px-[18px] py-4">
          <GoldSeal size={32} color="#E6C87A" />
          <div className="min-w-0">
            <p className="font-display text-lg font-medium">Session complete</p>
            {complete.data ? (
              <p className="mt-1 font-mono text-[0.64rem] uppercase tracking-[0.14em] text-paper/60">
                Alignment practice · Success Score {complete.data.score.total}
              </p>
            ) : complete.isPending ? (
              <p className="mt-1 font-mono text-[0.64rem] uppercase tracking-[0.14em] text-paper/60">
                Recording your practice
              </p>
            ) : null}
          </div>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-3.5 pt-7">
        {status === 'idle' && (
          <button
            type="button"
            onClick={start}
            className={cn(darkPillGold, 'w-full shadow-[0_12px_30px_-10px_rgba(176,141,63,0.6)]')}
          >
            Begin
          </button>
        )}
        {status === 'running' && (
          <button type="button" onClick={pause} className={cn(darkPillGlass, 'w-full')}>
            Pause
          </button>
        )}
        {status === 'paused' && (
          <div className="flex items-center gap-3">
            <button type="button" onClick={resume} className={cn(darkPillGold, 'flex-1')}>
              Resume
            </button>
            <Link to="/align/breathwork" className={cn(darkPillGlass, 'bg-transparent')}>
              End
            </Link>
          </div>
        )}
        {status === 'done' && (
          <div className="flex items-center gap-3">
            <button type="button" onClick={reset} className={cn(darkPillGlass, 'flex-1')}>
              Breathe again
            </button>
            <Link to="/align" className={darkPillGold}>
              Back to Align
            </Link>
          </div>
        )}
        <p className="text-center text-xs leading-normal text-paper/45">
          Breathe gently and never force a hold. Stop if you feel dizzy.
        </p>
      </div>
    </ImmersiveFrame>
  );
}
